/**
 * PRA-023: when a Lattice clinician changes a practitioner's formula, the change is shown as a
 * diff. Acknowledgment is required before the patient is invited (Q-013 default).
 */
import { useEffect, useState } from "react";
import type { ClinicFormulaChange, ClinicPatientList } from "@lattice/core";
import { Banner } from "@lattice/ui/Banner";
import { Button } from "@lattice/ui/Button";
import { EmptyState } from "@lattice/ui/EmptyState";
import { Pending } from "@lattice/ui/Pending";
import { Section } from "@lattice/ui/Section";
import { Text } from "@lattice/ui/Text";
import { acknowledgeClinicFormulaChanges, loadClinicFormulaChanges } from "../api.ts";
import { NotOpened } from "./NotOpened.tsx";
import { RecordList, RecordRow } from "./Record.tsx";
import { ClinicShell } from "./Shell.tsx";

const CHANGE: Readonly<Record<ClinicFormulaChange["diff"][number]["change"], string>> = {
  added: "Added",
  removed: "Removed",
  dose_changed: "Dose changed",
};

type Screen =
  | { readonly kind: "loading" }
  | { readonly kind: "missing" }
  | { readonly kind: "refused"; readonly why: "forbidden" | "failed" }
  | { readonly kind: "ready"; readonly change: ClinicFormulaChange };

function doseLine(dose: number | null, unit: string): string {
  return dose === null ? "none" : `${String(dose)} ${unit}`;
}

export function ClinicFormulaChangesPage({
  patientId,
  clinic,
}: {
  readonly patientId: string;
  readonly clinic: ClinicPatientList;
}) {
  const [screen, setScreen] = useState<Screen>({ kind: "loading" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void loadClinicFormulaChanges(patientId).then(
      (loaded) => {
        if (loaded.status === "forbidden" || loaded.status === "failed")
          setScreen({ kind: "refused", why: loaded.status });
        else if (loaded.status !== "ok") setScreen({ kind: "missing" });
        else setScreen({ kind: "ready", change: loaded.change });
      },
      () => setScreen({ kind: "refused", why: "failed" }),
    );
  }, [patientId]);

  if (screen.kind === "loading")
    return (
      <ClinicShell title="Physician changes" signedIn>
        <Pending message="Loading the changes" bones={["title", "line", "line"]} />
      </ClinicShell>
    );

  if (screen.kind === "refused")
    return (
      <NotOpened
        title={
          screen.why === "forbidden"
            ? "Your role can't see these changes"
            : "We couldn't load the changes"
        }
        body={
          screen.why === "forbidden"
            ? "Physician changes are for the clinic's practitioners."
            : "Try again in a minute. If it keeps happening, write to support."
        }
        retry={screen.why === "failed"}
        back={{ label: "Back to formula", href: `/clinic/patients/${patientId}/formula` }}
      />
    );

  if (screen.kind === "missing")
    return (
      <NotOpened
        title="No physician changes"
        body="There is no edited formula to review for this patient."
        back={{ label: "Back to formula", href: `/clinic/patients/${patientId}/formula` }}
      />
    );

  const { change } = screen;

  async function acknowledge() {
    setBusy(true);
    setError(null);
    const done = await acknowledgeClinicFormulaChanges(patientId).catch(() => false);

    setBusy(false);

    if (!done) {
      setError("We couldn't record that you reviewed the changes.");

      return;
    }

    const loaded = await loadClinicFormulaChanges(patientId);

    if (loaded.status === "ok") setScreen({ kind: "ready", change: loaded.change });
  }

  return (
    <ClinicShell
      title="Physician changes"
      description={`${change.patientName ?? "This patient"}: a Lattice physician edited the formula you submitted.`}
      crumbs={[
        { label: "Patients", href: "/clinic/patients" },
        { label: change.patientName ?? "Patient", href: `/clinic/patients/${patientId}` },
        { label: "Formula", href: `/clinic/patients/${patientId}/formula` },
      ]}
      signedIn
      clinicName={clinic.organizationName}
    >
      <Section title="What changed">
        {change.diff.length === 0 ? (
          <EmptyState title="No ingredient changes." />
        ) : (
          <RecordList label="What changed" testId="formula-diff">
            {change.diff.map((entry) => (
              <RecordRow
                key={`${entry.change}-${entry.ingredientId}`}
                title={`${CHANGE[entry.change]}: ${entry.name}`}
                detail={`${doseLine(entry.fromDose, entry.unit)} to ${doseLine(entry.toDose, entry.unit)}`}
              />
            ))}
          </RecordList>
        )}
      </Section>
      <Section title="Physician reasons">
        {change.edits.length === 0 ? (
          <EmptyState title="No reasons recorded." />
        ) : (
          <RecordList label="Physician reasons">
            {change.edits.map((edit) => (
              <RecordRow
                key={`${edit.change}-${edit.ingredientId}`}
                title={`${edit.name}: ${edit.reasonCode.replaceAll("_", " ")}`}
                detail={edit.note ?? undefined}
              />
            ))}
          </RecordList>
        )}
      </Section>
      {clinic.roles.includes("practitioner") && !change.acknowledged ? (
        <Section title="Review">
          <Text tone="secondary">The patient is not invited until you review these changes.</Text>
          <div>
            <Button
              testId="acknowledge-changes"
              corners="rounded"
              loading={busy}
              onClick={() => void acknowledge()}
            >
              I have reviewed the changes
            </Button>
          </div>
        </Section>
      ) : (
        <Banner tone="ok" title="Reviewed">
          You have reviewed these changes.
        </Banner>
      )}
      {error === null ? null : (
        <Banner tone="danger" title="Not recorded">
          {error}
        </Banner>
      )}
    </ClinicShell>
  );
}
